/**
 * Where a figure comes from, and how it was arrived at (owner request 2026-09-10).
 *
 * Every number in this product is served out of one snapshot, and a reader looking at `Cov %`
 * beside `TOA` beside `#SUBS` has no way to tell that the three were counted off three different
 * tables under three different rules. That is the gap this closes: the hover panel a figure
 * already opens now ends with one plain sentence saying how the number is counted, and one line
 * naming the system it came from.
 *
 * ## Two sources, and only two
 *
 * Everything reaches BigQuery, so "BigQuery" names nothing a reader can act on. What separates
 * the figures is the system upstream of it: **Nexus** dumps the jobs, submittals and candidate
 * records through its API, and **Run Rate** is its own book of placements.
 *
 * ⚠ **Three families are Run Rate by owner ruling rather than by table** (2026-09-10). The money
 * columns are summed out of `ch_gp_data` and the headcount columns off the alignment roster
 * `Region_alignment_enriched` — neither is literally `ch_run_rate`. The owner's call is that all
 * three read `Run Rate` today and that the backend will be reorganised to match; so this is a
 * deliberate simplification with a known expiry, not a mistake to tidy up. Splitting them out
 * into `GP Data` and `Alignment` here would put four source names in front of a reader who was
 * promised two, and would then have to be un-split when the backend moves.
 *
 * ## Why the map is keyed on the metric key, not the label
 *
 * The same figure is labelled four ways across the product — `ACTV`, `TOA`, `Active TOA`,
 * `Active Travelers on Assignment` — and a label is a presentation decision that moves. The
 * metric key is what the ingest wrote and what every surface indexes its block by, so keying on
 * it is what makes a table cell, a KPI tile and a chart bubble incapable of describing one
 * number two ways.
 *
 * ⚠ **A key absent from this map renders no footer at all** rather than a guess. A wrong
 * provenance line is worse than none: it is an assertion about lineage, printed in the one place
 * a reader goes when they do not trust a number.
 */

/**
 * ⚠ `audit` is a THIRD source name, for the Audit page ONLY (spec 007, FR-AU25, PRD-Audit
 * §6.5.3). Those figures are another system's verdicts — the Nightly Audit Results service's —
 * and calling them "Nexus" would be a true sentence under the wrong number. It is not in the map
 * below: an audit footnote is built from the audit's own rule line by `auditProvenance`, and
 * `tests/provenance.test.tsx` fails if anything outside `components/Audit/**` builds one.
 */
export type MetricSource = 'nexus' | 'runRate' | 'audit';

export interface MetricProvenance {
  /**
   * In display order. Two entries only where the figure genuinely divides one system by the
   * other — today that is Conv % alone, offers over submissions.
   */
  readonly sources: readonly MetricSource[];
  /**
   * How the figure is counted, in business language — **short** (owner request 2026-09-10).
   *
   * A tooltip is read in the second before the reader looks back at the number, so this is a
   * caption and not a paragraph: a dozen words, one clause per idea. The first cut ran to three
   * lines a cell and the panel started competing with the figure it was explaining. `TOA` wants
   * "Travelers on assignment on the as-of date", not an essay about stocks.
   *
   * Not the code and not the column names: "Offers ÷ submissions" rather than
   * `offersIn / subsIn`. The reader is the one asking "why is this 4%".
   *
   * It states the **rule**, never the filters — the period, region, Source, Targeting and
   * Specialty controls are on screen and already say what they narrow. A sentence repeating them
   * would go stale the moment a control moves, in a panel that cannot re-read it.
   */
  readonly formula: string;
}

export const SOURCE_LABEL: Record<MetricSource, string> = {
  nexus: 'Nexus',
  runRate: 'Run Rate',
  audit: 'Audit service',
};

const AUDIT: readonly MetricSource[] = ['audit'];

/** The footer under an Audit page figure: the audit's rule in one line, then `Audit service`. */
export function auditProvenance(rule: string): MetricProvenance {
  return { sources: AUDIT, formula: rule };
}

const NEXUS: readonly MetricSource[] = ['nexus'];
const RUN_RATE: readonly MetricSource[] = ['runRate'];
const BOTH: readonly MetricSource[] = ['nexus', 'runRate'];

/**
 * `Nexus`, `Run Rate`, or `Nexus + Run Rate`.
 *
 * `+` rather than a comma or "and": the two are being added as inputs to one figure, and on a
 * one-line footer in a 168px panel a symbol reads faster than a conjunction.
 */
export function sourceFace(sources: readonly MetricSource[]): string {
  return sources.map((s) => SOURCE_LABEL[s]).join(' + ');
}

/**
 * The same thing as a sentence, for an accessible name or description.
 *
 * Spelled out where the face is not, for the reason `#JOBS` renders `164(176)` and says
 * `164 (176)`: only the face is width-bound, and "Nexus plus Run Rate" read aloud is a sentence
 * where "Nexus + Run Rate" is a token.
 */
export function sourceSpoken(sources: readonly MetricSource[]): string {
  return sources.map((s) => SOURCE_LABEL[s]).join(' and ');
}

/** The panel footer as one spoken string — the formula, then the source. */
export function provenanceSpoken(provenance: MetricProvenance): string {
  return `${provenance.formula} Source: ${sourceSpoken(provenance.sources)}.`;
}

/**
 * The map. One entry per metric key the API serves, plus the aliases below it.
 *
 * ⚠ Sentences describe the **stored rule**, so they have to move when the rule does. Every one
 * of them is a claim the invariants in `CLAUDE.md` are the authority on — a figure whose
 * definition changes without this line changing puts a false explanation under a true number.
 */
const PROVENANCE: Record<string, MetricProvenance> = {
  // -------------------------------------------------------------------------
  // Placements — run rate
  // -------------------------------------------------------------------------
  activeToa: {
    sources: RUN_RATE,
    formula: 'Travelers on assignment on the as-of date.',
  },
  toaInternal: {
    sources: RUN_RATE,
    formula: 'Active travelers whose recruiter held this region and half on the start date.',
  },
  toaTarget: {
    sources: RUN_RATE,
    formula: 'Active travelers at a target account.',
  },
  onboarding: {
    sources: RUN_RATE,
    formula: 'Placements booked, not yet started. As of today.',
  },
  offers: {
    sources: RUN_RATE,
    formula: 'Offers extended in the period.',
  },
  offersInternal: {
    sources: RUN_RATE,
    formula: 'Offers whose recruiter held this region and half on the offer date.',
  },
  offersTarget: {
    sources: RUN_RATE,
    formula: 'Offers at a target account.',
  },
  starts: {
    sources: RUN_RATE,
    formula: 'Assignments started in the period.',
  },
  startsInternal: {
    sources: RUN_RATE,
    formula: 'Starts whose recruiter held this region and half on the start date.',
  },
  startsTarget: {
    sources: RUN_RATE,
    formula: 'Starts at a target account.',
  },
  terms: {
    sources: RUN_RATE,
    formula: 'Assignments ended in the period.',
  },
  backouts: {
    sources: RUN_RATE,
    formula: 'Placements that backed out before starting.',
  },

  // -------------------------------------------------------------------------
  // Money — summed out of the GP tables over run-rate placements
  // -------------------------------------------------------------------------
  gm: {
    sources: RUN_RATE,
    formula: 'Gross margin on assignments started in the period.',
  },
  gmInternal: {
    sources: RUN_RATE,
    formula: 'Gross margin where the recruiter held this region and half on the start date.',
  },
  gmTarget: {
    sources: RUN_RATE,
    formula: 'Gross margin at target accounts.',
  },
  gp: {
    sources: RUN_RATE,
    formula: 'Gross profit invoiced in the period.',
  },
  avgGm: {
    sources: RUN_RATE,
    formula: 'Gross margin ÷ active travelers.',
  },

  /*
   * ⚠ **The by-client breakdown's Margin / Avg Margin, and NOT `gm` / `avgGm` above** (owner
   * decision 2026-09-14). Those two still describe Delivery's GM, which is the `ch_gp_data`
   * SKU join over assignments started in the period; this pair is `ch_run_rate.NET_MARGIN`
   * summed over the active TOA population. One key cannot carry both sentences, and a wrong
   * footer costs more than a missing one — so `<ClientBreakdown>` passes these keys for
   * provenance while still reading the metric out of `gm` / `avgGm`.
   *
   * ⚠ They collapse back into `gm` / `avgGm` the day Delivery's column follows.
   */
  netMargin: {
    sources: RUN_RATE,
    formula: 'Net margin per hour, summed over active travelers.',
  },
  avgNetMargin: {
    sources: RUN_RATE,
    formula: 'Net margin ÷ active travelers, per hour.',
  },

  // -------------------------------------------------------------------------
  // People — the recruiter alignment roster
  // -------------------------------------------------------------------------
  recruiters: {
    sources: RUN_RATE,
    formula: 'Recruiters on the alignment roster. A headcount, so no period applies.',
  },
  recruitersNursing: {
    sources: RUN_RATE,
    formula: 'Recruiters aligned to Nursing.',
  },
  recruitersAllied: {
    sources: RUN_RATE,
    formula: 'Recruiters aligned to Allied.',
  },
  leaderCount: {
    sources: RUN_RATE,
    formula: 'Leaders on the alignment roster.',
  },
  managerCount: {
    sources: RUN_RATE,
    formula: 'Immediate managers on the alignment roster.',
  },
  managers: {
    sources: RUN_RATE,
    formula: 'Manager seats. Never added into the recruiter count.',
  },
  /**
   * The two **paired** cells on Region Detail's summary bar — `12 / 7`, one figure on each side.
   *
   * Their own entries rather than the left-hand figure's, because a footer reading "Recruiters
   * whose alignment rows state Nursing" under a cell showing both halves describes half of what
   * is on screen. Same reason the map is keyed on the metric and not the label: the sentence has
   * to be true of the thing the reader is looking at.
   */
  recruitersByOffering: {
    sources: RUN_RATE,
    formula: 'Recruiters by alignment — Nursing first, Allied second.',
  },
  targetAccounts: {
    sources: RUN_RATE,
    formula: 'Accounts marked Primary or Secondary.',
  },

  // -------------------------------------------------------------------------
  // Jobs — Nexus
  // -------------------------------------------------------------------------
  jobCount: {
    sources: NEXUS,
    formula: 'Open jobs posted in the period. Openings in brackets.',
  },
  openingsSum: {
    sources: NEXUS,
    formula: 'Openings on those jobs — one requisition for six nurses is six.',
  },
  jobsNursing: {
    sources: NEXUS,
    formula: 'Nursing jobs posted in the period.',
  },
  jobsAllied: {
    sources: NEXUS,
    formula: 'Allied jobs posted in the period.',
  },

  // -------------------------------------------------------------------------
  // Submissions — Nexus
  // -------------------------------------------------------------------------
  subs: {
    sources: NEXUS,
    formula: 'Submittals sent to the client in the period. A later rejection still counts.',
  },
  uniqueSubs: {
    sources: NEXUS,
    formula: 'Distinct candidates behind those submissions.',
  },
  subsInternal: {
    sources: NEXUS,
    formula: 'Submissions whose recruiter held this region and half on the day it was created.',
  },
  subsTarget: {
    sources: NEXUS,
    formula: 'Submissions at a target account.',
  },

  // -------------------------------------------------------------------------
  // The candidate book — Nexus
  // -------------------------------------------------------------------------
  bob: {
    sources: NEXUS,
    formula: 'Candidates owned by the recruiters in scope. As of today.',
  },
  candidates: {
    sources: NEXUS,
    formula: 'Candidate records in the book. As of today.',
  },
  pending: {
    sources: NEXUS,
    formula: 'Candidates with the status Pending.',
  },
  prospect: {
    sources: NEXUS,
    formula: 'Candidates with the status Prospect.',
  },
  pendingProspect: {
    sources: NEXUS,
    formula: 'Candidates by status — Pending first, Prospect second.',
  },
  prevEmployed: {
    sources: NEXUS,
    formula: 'Candidates with the status Previously Employed.',
  },

  // -------------------------------------------------------------------------
  // The KPI strip's own readings
  //
  // ⚠ Two of these are NOT the table column of the same name, which is why they are separate
  // entries rather than aliases. The strip answers to nothing (FR-029), so its Jobs card counts
  // open jobs across the whole table where `#JOBS` counts jobs posted inside the period and
  // inside the seven regions; and its Submissions card totals the window the chart above it is
  // drawing. Aliasing either to the table's key would put a true sentence under the wrong
  // number — the exact failure this file exists to prevent.
  // -------------------------------------------------------------------------
  clusters: {
    sources: RUN_RATE,
    formula: 'The five numbered regions plus the special project books.',
  },
  kpiJobs: {
    sources: NEXUS,
    formula: 'All open Nursing and Allied jobs, book-wide. Not narrowed by region or period.',
  },
  kpiSubmissions: {
    sources: NEXUS,
    formula: 'Submissions over the window the chart above is drawing.',
  },

  // -------------------------------------------------------------------------
  // Ratios
  // -------------------------------------------------------------------------
  covPct: {
    sources: NEXUS,
    formula: 'Submissions ÷ openings, capped at one per opening.',
  },
  convPct: {
    sources: BOTH,
    formula: 'Offers ÷ submissions. Offers from Run Rate, submissions from Nexus.',
  },
  conversion: {
    sources: BOTH,
    formula: 'Offers ÷ submissions. Offers from Run Rate, submissions from Nexus.',
  },
};

/**
 * Aliases — one figure that the API serves under a different key on a different surface.
 *
 * They exist because the surfaces were written at different times, not because the figures
 * differ. Pointing them at the same entry is what stops the by-client table and the region table
 * explaining the same number two ways; renaming the wire keys instead is a change to the
 * contracts and the stored documents, which this is not the moment for.
 */
const ALIAS: Record<string, string> = {
  // `<ClientBreakdown>` and the chart both serve TOA as `toa`.
  toa: 'activeToa',
  // `<ClientBreakdown>`'s job count, and the chart's JOBS series.
  jobs: 'jobCount',
  // The KPI strip's Active card — the same as-of stock the ACTV column reads, summed over the
  // seven regions. Its Jobs and Submissions cards are NOT aliases; see the entries above.
  active: 'activeToa',
};

/**
 * The provenance for a metric key, or `undefined` where there is none.
 *
 * Callers render nothing on `undefined` — see the note at the top of this file on why a guess is
 * worse than a gap.
 */
export function provenanceFor(key: string | null | undefined): MetricProvenance | undefined {
  if (!key) return undefined;
  return PROVENANCE[key] ?? PROVENANCE[ALIAS[key] ?? ''];
}

/** Every key with an entry, aliases included — the surface `tests/provenance.test.ts` walks. */
export function provenanceKeys(): string[] {
  return [...Object.keys(PROVENANCE), ...Object.keys(ALIAS)].sort();
}
